import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Figma (node 183:74 — Textarea):
 * Container: rounded-lg (10px), px-2.5 (10px), py-2 (8px), min-h-[64px], border (1px)
 * Default: bg-background, border-input
 * Focus: border-ring, shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]
 * Disabled: bg-input/50, opacity-50
 * Error: border-destructive
 * Error+Focus: border-destructive, shadow-[0px_0px_0px_3px_rgba(220,38,38,0.2)]
 * Text: text-sm (14px), font-normal (400), leading-5 (20px), text-foreground
 * Placeholder: text-muted-foreground
 */

const textareaVariants = cva(
  [
    'flex w-full rounded-lg border px-2.5 py-2 text-sm leading-5 text-foreground',
    'placeholder:text-muted-foreground focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-input/50',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-background border-input',
          'focus-visible:border-ring focus-visible:shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]',
        ].join(' '),
        error: [
          'bg-background border-destructive',
          'focus-visible:shadow-[0px_0px_0px_3px_rgba(220,38,38,0.2)]',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface IntuiTextareaProps
  extends Omit<React.ComponentProps<'textarea'>, 'ref'>,
    VariantProps<typeof textareaVariants> {
  /** Grow the textarea height as the user types */
  autoResize?: boolean
  /** Minimum number of visible text rows (default: 3). Only applies when autoResize is true. */
  minRows?: number
  /** Maximum number of visible text rows (only applies when autoResize is true) */
  maxRows?: number
  /** Show a character count below the textarea */
  showCount?: boolean
}

const LINE_HEIGHT = 20 // text-sm leading-5
const PADDING_Y = 16 // py-2 × 2

const IntuiTextarea = React.forwardRef<HTMLTextAreaElement, IntuiTextareaProps>(
  (
    {
      className,
      variant = 'default',
      autoResize = false,
      minRows = 3,
      maxRows,
      showCount = false,
      maxLength,
      onChange,
      value,
      defaultValue,
      ...props
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLTextAreaElement | null>(null)
    const [charCount, setCharCount] = React.useState(() => {
      return String(value ?? defaultValue ?? '').length
    })

    React.useEffect(() => {
      if (value !== undefined) {
        setCharCount(String(value).length)
      }
    }, [value])

    const minHeight = minRows * LINE_HEIGHT + PADDING_Y
    const maxHeight = maxRows ? maxRows * LINE_HEIGHT + PADDING_Y : undefined

    const resize = React.useCallback(() => {
      const textarea = internalRef.current
      if (!textarea || !autoResize) return
      textarea.style.height = 'auto'
      const clamped = maxHeight
        ? Math.min(textarea.scrollHeight, maxHeight)
        : textarea.scrollHeight
      textarea.style.height = `${Math.max(clamped, minHeight)}px`
    }, [autoResize, maxHeight, minHeight])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length)
      resize()
      onChange?.(e)
    }

    React.useEffect(() => {
      resize()
    }, [resize, value])

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref)
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node
      },
      [ref]
    )

    return (
      <div className="w-full">
        <textarea
          ref={setRefs}
          className={cn(
            textareaVariants({ variant }),
            autoResize ? `resize-none ${maxRows ? '' : 'overflow-hidden'}`.trim() : 'min-h-[64px]',
            className
          )}
          style={
            autoResize
              ? {
                  minHeight: `${minHeight}px`,
                  maxHeight: maxHeight ? `${maxHeight}px` : undefined,
                  overflow: maxHeight ? 'auto' : undefined,
                }
              : undefined
          }
          maxLength={maxLength}
          onChange={handleChange}
          {...(value !== undefined ? { value } : { defaultValue })}
          {...props}
        />
        {showCount && (
          <p className="text-xs text-muted-foreground mt-1.5 text-right">
            {charCount}
            {maxLength != null && `/${maxLength}`}
          </p>
        )}
      </div>
    )
  }
)

IntuiTextarea.displayName = 'IntuiTextarea'

export { IntuiTextarea, textareaVariants }
